import { supabase } from '../integrations/supabase/client';
import i18n from '../i18n';

// ✅ Updated predefined species after farm animal restructuring
export const PREDEFINED_SPECIES = {
  dog: 'Dogs',
  cat: 'Cats', 
  bird: 'Birds',
  fish: 'Fish',
  reptile: 'Reptiles',
  small_pet: 'Small Pets',
  horse: 'Horses',
  big_cat: 'Big Cats', // ✅ Felinos grandes (tigres, leones, panteras, etc.)
  cattle: 'Cattle',
  goat: 'Goats',
  sheep: 'Sheep',
  pig: 'Pigs',
  poultry: 'Poultry',
  donkey: 'Donkeys',
  mule: 'Mules',
  camelid: 'Camelids',
  other: 'Other Pets'
} as const;

// ✅ Removed farm_animal category - now these are individual species

export type SpeciesKey = keyof typeof PREDEFINED_SPECIES;

export interface CategoryPet {
  id: string;
  name: string;
  species: string;
  breed?: string;
  photo_url?: string;
  birth_date?: string;
  user_id: string;
  is_memorial: boolean;
  created_at: string;
  privacy_setting: string;
  users?: {
    id: string;
    full_name?: string;
    avatar_url?: string;
    email: string;
  };
  memory_posts?: Array<{ count: number }>;
}

// ✅ NEW: Species and Breeds interfaces for dynamic forms
export interface Species {
  id: number;
  code: string;
  name: string;
  created_at: string;
  updated_at: string;
}

export interface Breed {
  id: number;
  name: string;
  species_id: number;
  created_at: string;
  updated_at: string;
  sort_order: number;
  is_priority: boolean;
  original_name?: string;
}


// ✅ NEW: Helper function to translate species and breeds
function translateSpeciesName(code: string): string {
  const translationKey = `species.${code}`;
  const translated = i18n.t(translationKey);
  return translated !== translationKey ? translated : code;
}

function translateBreedName(name: string): string {
  // Convert breed name to translation key (lowercase, spaces to underscores)
  const key = name.toLowerCase().replace(/[\s\/\-\(\)]/g, '_').replace(/[^a-z0-9_]/g, '');
  const translationKey = `breeds.${key}`;
  const translated = i18n.t(translationKey);
  return translated !== translationKey ? translated : name;
}

// ✅ NEW: Dynamic species/breeds functions using direct SQL with translations
export async function getSpecies(): Promise<Species[]> {
  try {
    const { data, error } = await supabase
      .from('species' as any)
      .select('*')
      .order('name', { ascending: true });

    if (error) {
      console.error('Error fetching species:', error);
      throw error;
    }

    // Translate species names
    const translatedData = (data as unknown as Species[]).map(species => ({
      ...species,
      name: translateSpeciesName(species.code)
    }));
    
    return translatedData || [];
  } catch (error) {
    console.error('Error in getSpecies:', error);
    // Return fallback data based on existing predefined species
    return [
      { id: 1, code: 'dog', name: 'Dog', created_at: new Date().toISOString(), updated_at: new Date().toISOString() },
      { id: 2, code: 'cat', name: 'Cat', created_at: new Date().toISOString(), updated_at: new Date().toISOString() },
      { id: 3, code: 'bird', name: 'Bird', created_at: new Date().toISOString(), updated_at: new Date().toISOString() },
      { id: 4, code: 'fish', name: 'Fish', created_at: new Date().toISOString(), updated_at: new Date().toISOString() },
      { id: 5, code: 'reptile', name: 'Reptile', created_at: new Date().toISOString(), updated_at: new Date().toISOString() },
      { id: 6, code: 'small_pet', name: 'Small Pet', created_at: new Date().toISOString(), updated_at: new Date().toISOString() },
      { id: 7, code: 'horse', name: 'Horse', created_at: new Date().toISOString(), updated_at: new Date().toISOString() },
      { id: 60, code: 'big_cat', name: 'Big Cats', created_at: new Date().toISOString(), updated_at: new Date().toISOString() },
      { id: 33, code: 'cattle', name: 'Cattle', created_at: new Date().toISOString(), updated_at: new Date().toISOString() },
      { id: 34, code: 'goat', name: 'Goat', created_at: new Date().toISOString(), updated_at: new Date().toISOString() },
      { id: 35, code: 'sheep', name: 'Sheep', created_at: new Date().toISOString(), updated_at: new Date().toISOString() },
      { id: 36, code: 'pig', name: 'Pig', created_at: new Date().toISOString(), updated_at: new Date().toISOString() },
      { id: 37, code: 'poultry', name: 'Poultry', created_at: new Date().toISOString(), updated_at: new Date().toISOString() },
      { id: 38, code: 'donkey', name: 'Donkey', created_at: new Date().toISOString(), updated_at: new Date().toISOString() },
      { id: 39, code: 'mule', name: 'Mule', created_at: new Date().toISOString(), updated_at: new Date().toISOString() },
      { id: 40, code: 'camelid', name: 'Camelid', created_at: new Date().toISOString(), updated_at: new Date().toISOString() },
      { id: 8, code: 'other', name: 'Other', created_at: new Date().toISOString(), updated_at: new Date().toISOString() }
    ];
  }
}

export async function getBreedsBySpecies(speciesId: number): Promise<Breed[]> {
  try {
    const { data, error } = await supabase
      .from('breeds' as any)
      .select('*')
      .eq('species_id', speciesId)
      .order('is_priority', { ascending: false })
      .order('sort_order', { ascending: true })
      .order('name', { ascending: true });

    if (error) {
      console.error('Error fetching breeds:', error);
      throw error;
    }

    // Translate breed names and keep original for matching
    const translatedData = (data as unknown as Breed[]).map((breed) => ({
      ...breed,
      original_name: breed.name,
      name: translateBreedName(breed.name),
    }));
    
    return translatedData || [];
  } catch (error) {
    console.error('Error in getBreedsBySpecies:', error);
    return [];
  }
}

// ✅ Helper function to find breed by name (for compatibility with existing data)
export async function findBreedByName(breedName: string, speciesId?: number): Promise<Breed | null> {
  try {
    let query = supabase
      .from('breeds' as any)
      .select('*')
      .ilike('name', breedName);

    if (speciesId) {
      query = query.eq('species_id', speciesId);
    }

    const { data, error } = await query.limit(1).maybeSingle();

    if (error) {
      console.error('Error finding breed by name:', error);
      return null;
    }

    return (data as unknown as Breed) || null;
  } catch (error) {
    console.error('Error in findBreedByName:', error);
    return null;
  }
}

export async function getPublicPetsByCategory(category?: string): Promise<CategoryPet[]> {
  try {
    console.log('Fetching pets for category:', category);
    
    let query = supabase
      .from('pets')
      .select(`
        id,
        name,
        species,
        breed,
        photo_url,
        birth_date,
        user_id,
        is_memorial,
        created_at,
        privacy_setting,
        users:user_id (
          id,
          full_name,
          avatar_url,
          email
        )
      `)
      .eq('privacy_setting', 'public')
      .order('created_at', { ascending: false });

    // Apply species filter if specified
    if (category && category !== 'all') {
      if (category === 'cattle') {
        // For cattle, query both 'cattle' and legacy 'cow' species
        query = query.in('species', ['cattle', 'cow']);
      } else if (category === 'other') {
        // For other, query all species not explicitly displayed as tiles
        const displayedSpecies = ['dog', 'cat', 'bird', 'fish', 'reptile', 'small_pet', 'horse', 'cattle', 'cow'];
        query = query.not('species', 'in', `(${displayedSpecies.join(',')})`);
      } else {
        query = query.eq('species', category);
      }
    }

    const { data, error } = await query;

    if (error) {
      console.error('Error fetching pets by category:', error);
      throw error;
    }

    console.log('Fetched pets data:', data);
    return data || [];
  } catch (error) {
    console.error('Error in getPublicPetsByCategory:', error);
    throw error;
  }
}

export async function getPetCategoryCounts(): Promise<Record<string, number>> {
  try {
    const { data, error } = await supabase
      .from('pets')
      .select('species')
      .eq('privacy_setting', 'public');

    if (error) {
      console.error('Error fetching pet category counts:', error);
      throw error;
    }

    // Count pets by species - only count categories that are displayed as tiles
    const categoryCounts: Record<string, number> = {};
    
    // Initialize only displayed categories with 0
    const displayedCategories = ['dog', 'cat', 'bird', 'fish', 'reptile', 'small_pet', 'horse', 'cattle', 'other', 'all'];
    displayedCategories.forEach(key => {
      categoryCounts[key] = 0;
    });

    // Define which species belong to displayed categories (same as filtering logic)
    const displayedSpecies = ['dog', 'cat', 'bird', 'fish', 'reptile', 'small_pet', 'horse', 'cattle', 'cow'];

    // Count actual pets by species with mapping for legacy data
    data?.forEach(pet => {
      if (pet.species) {
        // Map legacy "cow" to "cattle"
        const mappedSpecies = pet.species === 'cow' ? 'cattle' : pet.species;
        
        if (displayedSpecies.includes(pet.species) || mappedSpecies === 'cattle') {
          // Count in the appropriate displayed category
          if (categoryCounts.hasOwnProperty(mappedSpecies)) {
            categoryCounts[mappedSpecies]++;
          }
        } else {
          // All other species (including camelid/alpaca) go to "other"
          categoryCounts['other']++;
        }
      }
    });

    // Add total count for 'all'
    categoryCounts['all'] = data?.length || 0;

    console.log('Category counts:', categoryCounts);
    return categoryCounts;
  } catch (error) {
    console.error('Error in getPetCategoryCounts:', error);
    return {};
  }
}

// Keep the legacy function for backward compatibility
export async function getPetsByCategory(category: string): Promise<CategoryPet[]> {
  return getPublicPetsByCategory(category);
}

export async function getAllCategories() {
  try {
    const { data, error } = await supabase
      .from('pets')
      .select('species')
      .eq('privacy_setting', 'public');

    if (error) {
      console.error('Error fetching categories:', error);
      throw error;
    }

    // Count pets by species (no more farm animal mapping)
    const categoryCounts: Record<string, number> = {};
    data?.forEach(pet => {
      if (pet.species) {
        categoryCounts[pet.species] = (categoryCounts[pet.species] || 0) + 1;
      }
    });

    // Return categories with counts, including only predefined species
    return Object.entries(PREDEFINED_SPECIES).map(([key, label]) => ({
      id: key,
      name: label,
      count: categoryCounts[key] || 0,
      pets: `${categoryCounts[key] || 0} pets`
    }));
  } catch (error) {
    console.error('Error in getAllCategories:', error);
    throw error;
  }
}
